import moment from "moment";
import i18n from "src/i18n";
import type { PageMeta } from "src/api/public-types";
import type { VisitorGroupListItem, VisitorGroupListParams, VisitorInviteGroupPageListParams, VisitorListItem } from "src/api/visitor/visitor-types";
import { convertKoreanPhoneNumber, ViewYmdFormat } from "src/utils/common-util";
import type {
  VisitorHistoryDateRange,
  VisitorHistoryExtraFields,
  VisitorHistoryGroup,
  VisitorHistoryPage,
  VisitorSearchContext,
} from "../types/visitorHistory";

export const isSelectVisitorHistoryDateRange = ([startDate, endDate]: VisitorHistoryDateRange) => Boolean(startDate && endDate);

const hasRequestValue = (value: number | string | null | undefined) => value !== undefined && value !== null && value !== "";

export const hasVisitorGroupId = (
  contractVisitorGroupId: VisitorGroupListItem["contractVisitorGroupId"],
): contractVisitorGroupId is number | string => {
  return hasRequestValue(contractVisitorGroupId);
};

export const createVisitorGroupInviteListParams = (
  contractVisitorGroupIds: VisitorGroupListItem["contractVisitorGroupId"][],
): VisitorGroupListParams => {
  return {
    contractVisitorGroupIds: contractVisitorGroupIds.filter(hasVisitorGroupId).join(","),
  };
};

const removeNilValues = <T extends Record<string, unknown>>(source: T) => {
  return Object.entries(source).reduce<Record<string, unknown>>((result, [key, value]) => {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }

    return result;
  }, {});
};

export const hasVisitorSearchContextTarget = (context?: VisitorSearchContext) => {
  if (!context) return false;

  return hasRequestValue(context.contractManageId);
};

export const createVisitorHistorySinglePage = (totalCount: number): VisitorHistoryPage => {
  return {
    currentPage: 0,
    totalPage: totalCount > 0 ? 1 : 0,
    totalCount,
  };
};

const parsePageMetaNumber = (value: number | string | undefined, fallback: number) => {
  if (value === undefined || value === null || value === "") return fallback;

  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : fallback;
};

export const createVisitorHistoryPageFromMeta = (pageMeta: PageMeta | undefined, fallbackTotalCount: number): VisitorHistoryPage => {
  if (!pageMeta) return createVisitorHistorySinglePage(fallbackTotalCount);

  return {
    currentPage: pageMeta.pageRequest.page || 0,
    totalPage: pageMeta.totalPages || 0,
    totalCount: parsePageMetaNumber(pageMeta.totalElements, fallbackTotalCount),
  };
};

export const createVisitorInviteGroupPageListParams = (
  context: VisitorSearchContext,
  dateRange: VisitorHistoryDateRange,
  page = 0,
  size = 20,
): VisitorInviteGroupPageListParams => {
  const [startDate, endDate] = dateRange;

  return {
    contractManageId: context.contractManageId,
    searchStartTime: moment(startDate).startOf("day").format(),
    searchEndTime: moment(endDate).endOf("day").format(),
    page,
    size,
    sort: {
      orders: [{ property: "createdDate", direction: "DESC" }],
    },
  };
};

export const normalizeVisitorGroupInviteList = (visitorGroups: VisitorGroupListItem[]): VisitorListItem[] => {
  return visitorGroups.flatMap((group) => {
    const groupFields = removeNilValues(group as unknown as Record<string, unknown>);
    const visitors = group.contractVisitorList || [];

    if (visitors.length === 0) {
      return [{ ...groupFields, contractVisitorList: undefined } as VisitorListItem];
    }

    return visitors.map((visitor) => {
      const visitorFields = removeNilValues(visitor as unknown as Record<string, unknown>);

      return {
        ...groupFields,
        ...visitorFields,
        contractVisitorList: undefined,
      } as VisitorListItem;
    });
  });
};

export const formatVisitApplyNumber = (visitApplyNumber?: string) => {
  if (!visitApplyNumber) {
    return "-";
  }

  return visitApplyNumber;
};

export const formatDateWithDay = (date?: string) => {
  if (!date) return "";

  const targetDate = moment(date);
  if (!targetDate.isValid()) return "";

  const weekdayLocale = i18n.language?.startsWith("en") ? "en" : "ko";
  const dayOfWeek = targetDate.clone().locale(weekdayLocale).format("ddd");

  return `${targetDate.format(ViewYmdFormat.YYYY_MM_DD)}(${dayOfWeek}) ${targetDate.format("HH:mm")}`;
};

export const formatVisitPeriod = (log: VisitorListItem) => {
  const startDate = moment(log.visitStartTime);
  const endDate = moment(log.visitEndTime);

  if (!log.visitStartTime || !startDate.isValid()) return "-";
  if (!log.visitEndTime || !endDate.isValid()) return formatDateWithDay(log.visitStartTime);

  const endText = startDate.isSame(endDate, "day") ? endDate.format("HH:mm") : formatDateWithDay(log.visitEndTime);

  return `${formatDateWithDay(log.visitStartTime)} ~ ${endText}`;
};

export const formatVisitDuration = (log: VisitorListItem) => {
  const startDate = moment(log.visitStartTime);
  const endDate = moment(log.visitEndTime);

  if (!log.visitStartTime || !log.visitEndTime || !startDate.isValid() || !endDate.isValid()) return "";

  const totalMinutes = endDate.diff(startDate, "minutes");
  if (totalMinutes <= 0) return "";

  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  const duration = [
    days > 0 ? i18n.t("detail.reservationDurationDay", { count: days, ns: "contract" }) : "",
    hours > 0 ? i18n.t("detail.reservationDurationHour", { count: hours, ns: "contract" }) : "",
    minutes > 0 ? i18n.t("detail.reservationDurationMinute", { count: minutes, ns: "contract" }) : "",
  ].filter(Boolean);

  return `(${duration.length > 0 ? duration.join(" ") : i18n.t("detail.reservationDurationMinute", { count: 1, ns: "contract" })})`;
};

export const getVisitorGroupKey = (log: VisitorListItem, index: number) => {
  if (log.contractVisitorGroupId) return `group-${log.contractVisitorGroupId}`;
  if (log.visitApplyNumber) return `apply-${log.visitApplyNumber}`;
  if (log.contractVisitorId) return `visitor-${log.contractVisitorId}`;

  return `visitor-index-${index}`;
};

export const createVisitorGroups = (visitorLogs: VisitorListItem[] = []) => {
  const groupMap = new Map<string, VisitorHistoryGroup>();

  visitorLogs.forEach((log, index) => {
    const key = getVisitorGroupKey(log, index);
    const group = groupMap.get(key);

    if (group) {
      group.visitors.push(log);
      return;
    }

    groupMap.set(key, {
      key,
      representative: log,
      visitors: [log],
    });
  });

  return Array.from(groupMap.values());
};

export const getVisitorGroupTitle = (visitors: VisitorListItem[]) => {
  const firstVisitorName = visitors[0]?.visitorName || "-";
  const otherVisitorCount = visitors.length - 1;

  return otherVisitorCount > 0
    ? i18n.t("groupTitleOthers", { name: firstVisitorName, count: otherVisitorCount, ns: "visitor" })
    : firstVisitorName;
};

export const getInvitationChannelLabel = (invitationChannel?: VisitorListItem["invitationChannel"]) => {
  switch (invitationChannel) {
    case "TAAP_SPACE":
      return "TaapSpace";
    case "MOBILE_APP":
      return "Mobile App";
    case "CTRL_ROOM":
      return "Ctrl.Room";
    case "SELF_REGIST":
      return "Self Registration";
    default:
      return "-";
  }
};

export const getVisitorContact = (visitor: VisitorListItem) => {
  if (visitor.visitorMobileNumber) return convertKoreanPhoneNumber(visitor.visitorMobileNumber);
  if (visitor.visitorEmail) return visitor.visitorEmail;

  return "-";
};

export const getVisitorCompanyName = (visitor: VisitorListItem) => {
  const extraFields = visitor as VisitorHistoryExtraFields;

  return extraFields.visitorCompanyName || extraFields.visitorOrganizationName || extraFields.organizationName || extraFields.companyName || "-";
};
